import { currentBootPhase, recordEventAndFlush, recordSpan, wasTabHiddenDuringSession } from "@/services/telemetry";
import { resetBootReloadStreak } from "./bootLoop";

/**
 * Reports the perceived boot outcome — the single most important perceived metric (login
 * experience) — as one report per page load:
 *
 * - `boot.interactive` (`perceived`): a span from navigation start to bootstrap completion
 *   (`markBootInteractive`, called when the boot's data flow finishes), tagged with the boot phase.
 * - `boot.abandoned` (`reliability`): emitted with a flush when the page tears down while a boot
 *   that began (`markBootBegan`) hasn't completed, so boots the user gave up on are countable.
 */

let bootBegan = false;
let reported = false;
let pageHideListener: ((event: PageTransitionEvent) => void) | null = null;

/** Called when a bootstrap run starts; latches "a boot actually began this page load". */
export function markBootBegan(): void {
  bootBegan = true;
}

/** Called when the boot's bootstrap flow completes; reports `boot.interactive` once. */
export function markBootInteractive(): void {
  if (reported || !bootBegan) return;
  reported = true;
  detach();
  // Reaching interactive breaks any reload loop — clear the streak so the next load starts fresh.
  resetBootReloadStreak();
  recordSpan("boot.interactive", new Date(performance.timeOrigin), {
    boot: {
      phase: currentBootPhase() ?? "unknown",
      durationMs: Math.round(performance.now()),
    },
    // Stamped explicitly (not only as a session attribute) so every boot.interactive span is
    // filterable: background-restored tabs report 12-hour "boots" that poison the percentiles.
    tab: { hidden_during_session: wasTabHiddenDuringSession() },
  });
}

function detach(): void {
  if (pageHideListener && typeof window !== "undefined") {
    window.removeEventListener("pagehide", pageHideListener);
  }
  pageHideListener = null;
}

/**
 * @returns A disposer that unsubscribes and detaches listeners (used by tests; the app never tears
 *   this down).
 */
export function initializeBootTelemetry(): () => void {
  bootBegan = false;
  reported = false;

  // A boot the user leaves before completion never reaches markBootInteractive; capture it as an
  // abandonment (with a flush, since the page is going away) so stalled boots are countable. Skip
  // bfcache freezes (`event.persisted`) — the page can be restored and finish the boot — and
  // require a boot to have actually begun so quick bounce visits don't dominate the event.
  pageHideListener = (event: PageTransitionEvent): void => {
    if (reported || event.persisted || !bootBegan) return;
    reported = true;
    detach();
    void recordEventAndFlush("boot.abandoned", {
      boot: {
        phase: currentBootPhase() ?? "unknown",
        elapsedMs: Math.round(performance.now()),
      },
      tab: { hidden_during_session: wasTabHiddenDuringSession() },
    });
  };

  if (typeof window !== "undefined" && pageHideListener) window.addEventListener("pagehide", pageHideListener);

  return () => {
    reported = true;
    detach();
  };
}
